import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useAlert } from "../../components/Alert";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import LoaderContainer from "../../components/LoaderContainer";

const useStyles = makeStyles({
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "400px",
    margin: "0 auto",
    padding: "16px",
  },
  submitButton: {
    marginTop: "16px",
  },
  selectField: {
    textAlign: "left",
  },
});

const InitiateSubscriptionInvoicing = () => {
  const classes = useStyles();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    farmer_id: "",
    farm_id: "",
  });
  const [farmers, setFarmers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [farmerLoading, setFarmerLoading] = useState(false);
  const [farmLoading, setFarmLoading] = useState(false);

  useEffect(() => {
    fetchFarmers();
  }, []);

  const fetchFarmers = async () => {
    setFarmerLoading(true);
    try {
      const response = await api.get(
        "/usermanagement/get-sub-users/?farmers&paginate=false"
      );
      // The structure is { data: { farmers: [...] } }
      if (response.data && response.data.data && response.data.data.farmers) {
        setFarmers(response.data.data.farmers || []);
      } else {
        console.error(
          "Unexpected farmers API response structure:",
          response.data
        );
        setFarmers([]);
      }
    } catch (err) {
      console.error("Error fetching farmers:", err);
      showAlert("error", t("Failed to load farmers list"));
      setFarmers([]);
    } finally {
      setFarmerLoading(false);
      setLoading(false);
    }
  };

  const fetchFarms = async (farmer_id) => {
    if (!farmer_id) {
      setFarms([]);
      return;
    }

    setFarmLoading(true);
    try {
      const response = await api.get(`/farmer/get-farmer-farms/${farmer_id}/`);
      if (response.data && response.data.data && response.data.data.farms) {
        setFarms(response.data.data.farms || []);
      } else {
        console.error(
          "Unexpected farms API response structure:",
          response.data
        );
        setFarms([]);
      }
    } catch (err) {
      console.error("Error fetching farms:", err);
      showAlert("error", t("Failed to load farms list"));
      setFarms([]);
    } finally {
      setFarmLoading(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "farmer_id" && value) {
      // Reset farm selection when farmer changes
      setFormData((prev) => ({ ...prev, farm_id: "", farmer_id: value }));
      await fetchFarms(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.farmer_id) {
      showAlert("error", t("Please select a farmer"));
      return;
    }

    if (!formData.farm_id) {
      showAlert("error", t("Please select a farm"));
      return;
    }

    // Navigate to subscription invoicing page with farm_id parameter
    navigate(`/subscription-invoicing?farm_id=${formData.farm_id}`);
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Box className={classes.formContainer}>
          <CustomTitle>{t("Initiate Subscription Invoicing")}</CustomTitle>

          <TextField
            label={t("Select Farmer")}
            name="farmer_id"
            select
            value={formData.farmer_id}
            onChange={handleChange}
            fullWidth
            className={classes.selectField}
            disabled={farmerLoading}
            InputProps={{
              endAdornment: farmerLoading ? (
                <CircularProgress size={20} />
              ) : null,
            }}
          >
            {farmers.map((farmer) => (
              <MenuItem key={farmer.id} value={farmer.id}>
                {farmer.full_name} ({farmer.phone_number})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t("Select Farm")}
            name="farm_id"
            select
            value={formData.farm_id}
            onChange={handleChange}
            fullWidth
            className={classes.selectField}
            disabled={!formData.farmer_id || farmLoading}
            InputProps={{
              endAdornment: farmLoading ? <CircularProgress size={20} /> : null,
            }}
          >
            {farms.map((farm) => (
              <MenuItem key={farm.id} value={farm.id}>
                {farm.farm_name} ({farm.area} acres)
              </MenuItem>
            ))}
          </TextField>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.submitButton}
            onClick={handleSubmit}
            disabled={!formData.farmer_id || !formData.farm_id}
          >
            {t("Next")}
          </Button>
        </Box>
      </LoaderContainer>
    </>
  );
};

export default InitiateSubscriptionInvoicing;
